/**
 * <cooked-choices> — holds the add button until every choice is made.
 * Added 2026-09-18; the Heritage Day gift box was the first use.
 *
 * Dawn's product-form.js submits by fetch and returns early when the submit
 * button carries aria-disabled="true" (product-form.js, onSubmitHandler).
 * The form is novalidate, so that attribute is the only guard that stops an
 * empty selection reaching the cart. This element sets it while any choice
 * <select> has no value and clears it once all do, shows or hides the hint
 * to match, and on a click while incomplete moves focus to the first empty
 * select so the buyer sees what is being asked. It never sets `disabled`:
 * a disabled button drops out of the tab order and gives a keyboard user no
 * clue why.
 *
 * product-form.js also sets aria-disabled during a submit and removes it on
 * completion. After a successful add the selections still stand, so the
 * button correctly comes back enabled. On an error it leaves aria-disabled
 * in place; the next change here re-evaluates.
 *
 * `slot`, `title` and `hidden` are real Element properties (correction log,
 * 2026-08-05), so instance state here uses names that are not.
 */
class CookedChoices extends HTMLElement {
  connectedCallback() {
    this.formEl = this.closest('form');
    if (!this.formEl) return;
    this.submitEl = this.formEl.querySelector('button[type="submit"]');
    this.hintEl = this.querySelector('[data-ca-choices-hint]');

    this.addEventListener('change', () => this.evaluate());
    if (this.submitEl) {
      this.submitEl.addEventListener('click', (event) => {
        const empty = this.firstEmpty();
        if (!empty) return;
        event.preventDefault();
        this.evaluate();
        empty.focus();
      });
    }
    this.evaluate();
  }

  selects() {
    return Array.from(this.querySelectorAll('select[data-ca-choice]'));
  }

  firstEmpty() {
    return this.selects().find((select) => select.value === '') || null;
  }

  evaluate() {
    const incomplete = this.firstEmpty() !== null;
    if (this.submitEl) {
      if (incomplete) {
        this.submitEl.setAttribute('aria-disabled', 'true');
        this.submitEl.classList.add('ca-pdp__add--waiting');
      } else {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('ca-pdp__add--waiting');
      }
    }
    if (this.hintEl) this.hintEl.hidden = !incomplete;
  }
}

if (!customElements.get('cooked-choices')) {
  customElements.define('cooked-choices', CookedChoices);
}
